import { createSelector, type MemoizedSelector } from '@ngrx/store';

import { runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElement,
  type CanvasElementId,
  type CanvasElementType,
  type LayoutData,
} from '@blaze-runtime/engine-domain';
import { DIALOG_OVERLAY_ID, DRAWER_OVERLAY_ID } from '@blaze-runtime/engine-domain';
import { type RuntimeEventType } from '@blaze-shared/schema-domain';

import { getActiveInputDataSource } from '../../canvas-elements/element-model/data-binding';
import { hasDataProps } from '../../canvas-elements/element-model/element-props-guards';
import { getFormInputNameByType } from '../../canvas-elements/element-model/io';
import { buildPath } from '../../utils/canvas-element/path';
import { pickParentElement } from '../../utils/layout/relationship';
import { getEngineState } from '../engine/engine-adapters';
import { engineAdapterSelectors } from '../engine/engine.selectors';

// TODO: test
export const getElementIds = createSelector(
  getEngineState,
  (state) => state.ids as CanvasElementId[],
);

// TODO: test
export const getElementEntities = createSelector(
  getEngineState,
  engineAdapterSelectors.selectEntities,
);

export const getElementsEntityState = createSelector(
  getElementIds,
  getElementEntities,
  (ids, entities) => ({ ids, entities }),
);

export const getElementById = (
  elementId: CanvasElementId,
): MemoizedSelector<object, CanvasElement | undefined> =>
  createSelector(getElementEntities, (entities) => entities[elementId]);

export const getElementTypeById = (
  elementId: CanvasElementId,
): MemoizedSelector<object, CanvasElementType | undefined> =>
  createSelector(getElementById(elementId), (element) => element?.type);

// TODO: test
export const getElements = createSelector(
  getEngineState,
  engineAdapterSelectors.selectEntitiesList,
);

// TODO: test
export const getLayoutIds = createSelector(getEngineState, (state) => state.layoutIds || []);

// TODO: test
export const getLayoutEntities = createSelector(
  getEngineState,
  (state) => state.layoutEntities || {},
);

// TODO: test
export const getOffSyncLayouts = createSelector(
  getEngineState,
  (state) => state.offSyncLayouts || {},
);

// TODO: test
export const hasOffSyncLayouts = createSelector(getEngineState, (state) =>
  state.offSyncLayouts ? Object.keys(state.offSyncLayouts).length > 0 : false,
);

// TODO: test
export const getLayoutById = (layoutId: string): MemoizedSelector<object, LayoutData> =>
  createSelector(getLayoutEntities, (entities) => entities[layoutId]);

// TODO: test
export const getOverlayIds = createSelector(getEngineState, (state) => state.overlays || []);

// TODO: test
export const getFirstOverlayHostedNs = createSelector(
  getOverlayIds,
  getElementEntities,
  (overlayIds, entities) =>
    overlayIds[0] ? entities[overlayIds[0]]?.slots?.main?.hosting?.ns : undefined,
);

export const getDialogOverlayHostedNs = createSelector(
  getOverlayIds,
  getElementEntities,
  (overlayIds, entities) => {
    if (overlayIds.includes(DIALOG_OVERLAY_ID)) {
      return entities[DIALOG_OVERLAY_ID]?.slots?.main?.hosting?.ns;
    } else if (overlayIds.includes(DRAWER_OVERLAY_ID)) {
      return entities[DRAWER_OVERLAY_ID]?.slots?.main?.hosting?.ns;
    }
    return undefined;
  },
);

// TODO: test
export const getSelectedPathNode = createSelector(
  getEngineState,
  (state) => state.selectedPathNode,
);

// TODO: test
export const getSelectedPath = createSelector(
  getElementEntities,
  getSelectedPathNode,
  (elements, pathNode) => buildPath(elements, pathNode) || [],
);

export const getSelectionData = createSelector(
  getSelectedPathNode,
  getSelectedPath,
  (selectedPathNode, selectedPath) => ({
    pathNode: selectedPathNode,
    path: selectedPath,
  }),
);

// TODO: test
export const getParentElementById = (
  elementId: CanvasElementId,
): MemoizedSelector<object, CanvasElement | undefined> =>
  createSelector(getElementEntities, (elements) => pickParentElement(elements, elementId));

/**
 * Finds the form to which the field element is bound to.
 * If element is not a field of any form - undefined will be returned
 * @param elementId the field element
 * @returns the id of the form
 */
export const getFormIdForField = (
  elementId: CanvasElementId,
): MemoizedSelector<object, string | undefined> =>
  createSelector(getElementEntities, (entities) => {
    const element = entities[elementId];
    if (hasDataProps(element)) {
      const dataSource = element.props.data.find(
        (d) => d.key === getFormInputNameByType(element.type),
      )?.dataSource;
      // If element is bound to a form - it means it is its field -> return the id of that form
      return dataSource?.sourceType === 'formContainer' ? dataSource.id : undefined;
    }
    return undefined;
  });

export const getElementsBoundToForm = (
  formElementId: CanvasElementId,
): MemoizedSelector<object, CanvasElement[]> =>
  createSelector(getElements, (elements) =>
    elements.filter((element) => {
      if (hasDataProps(element)) {
        const dataBinding = getActiveInputDataSource(element);
        return (
          (dataBinding?.sourceType === 'formContainer' ||
            dataBinding?.sourceType === 'editableList') &&
          dataBinding.id === formElementId
        );
      }
      return false;
    }),
  );

export const getHasRelatedElements = (
  elementId: CanvasElementId,
  eventType: RuntimeEventType,
): MemoizedSelector<object, boolean> =>
  createSelector(
    runtimeSelectors.selectConnections,
    (connections) =>
      !!connections.find(
        (c) =>
          c.source.layerType === 'Ui' && c.source.id === elementId && c.eventType === eventType,
      ),
  );
